import { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useStatsigClient } from '@statsig/react-bindings';

import { QUESTION_TYPES } from '@api/instant_survey';
import { maxQuestionError } from '@api/surveys';
import { GATES } from '@constants';
import {
  ActionOverlayPopover,
  Button,
  Card,
  DragDropV2Icon,
  IconButton,
  Layout,
  OverlayPopover,
  Pill,
  PlusIcon,
  ShuffleIcon,
  TrashIcon,
} from '@utilities';

import ModalAddConcept from './components/ModalAddConcept';
import ModalInstantSurveyQuestion from '../../../ModalInstantSurveyQuestion';
import ModalQuestionLibrary from '../../../ModalQuestionLibrary';
import Question from '../Question';
import {
  cleanupLogicAfterEdit,
  cleanupLogicAfterRemoval,
  duplicateSubQuestion,
} from '../../../../utilities/helpers';
import { SurveyContext } from '../../../../../../utilities/context';

import styles from './_index.module.scss';

export const SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX = 'sequential-monadic-subquestions';
export const SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE = 'SEQUENTIAL_MONADIC_SECTION';
const allowedQuestionTypes = [
  QUESTION_TYPES.RADIO,
  QUESTION_TYPES.CHECKBOX,
  QUESTION_TYPES.ESSAY,
  QUESTION_TYPES.INSTRUCTIONS,
  QUESTION_TYPES['TABLE-RADIO'],
];

const getImageUrl = (image) => image?.cdnUrl || image?.previewUrl || image?.url;

const SequentialMonadicBlock = ({
  canPreviewSurvey,
  firstErrorRef,
  isDetailedQuestionView,
  onPreviewSurvey,
  onRemoveQuestion,
  pipingQuestionsMap,
  provided,
  question,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const { currentQuestionCount, instantSurveyType, maxQuestions, survey, updateSurvey } =
    useContext(SurveyContext);
  const { client } = useStatsigClient();
  const [activeConceptActionsIndex, setActiveConceptActionsIndex] = useState(null);
  const [isAddConceptModalOpen, setIsAddConceptModalOpen] = useState(false);
  const [isAddSubQuestionPopoverOpen, setIsAddSubQuestionPopoverOpen] = useState(false);
  const [isCreateSubQuestionModalOpen, setIsCreateSubQuestionModalOpen] = useState(false);
  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isQuestionLibraryModalOpen, setIsQuestionLibraryModalOpen] = useState(false);
  const [selectedSubQuestion, setSelectedSubQuestion] = useState(null);
  const [selectedSubQuestionIndex, setSelectedSubQuestionIndex] = useState(null);
  const activeConcepts = (question.concepts || [])
    .map((concept, conceptIndex) => ({ concept, conceptIndex }))
    .filter(({ concept }) => !concept._delete);
  const activeSubQuestions = (question.subQuestions || []).filter(({ _delete }) => !_delete);
  const conceptCount = activeConcepts.length;
  const hasQuestionLibrary = client.checkGate(GATES.QUESTION_LIBRARY);
  const questionCount =
    activeSubQuestions.filter(
      (subQuestion) => subQuestion.type !== QUESTION_TYPES.INSTRUCTIONS.value
    ).length * conceptCount;
  const questionCountLabel = `${questionCount} question${questionCount === 1 ? '' : 's'}`;
  const addSubQuestionButtonRef = useRef(null);
  const optionsDropdown = useRef(null);
  const randomizationLabel = question.properties?.randomizeSubQuestions ? 'Randomized. ' : '';
  const subQuestionDroppableId = `${SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_PREFIX}-${question.id}`;
  const shouldDisableSubQuestionDrag =
    !showActions || activeSubQuestions.length <= 1 || question.properties?.randomizeSubQuestions;

  const canAddSubQuestion =
    conceptCount === 0 || currentQuestionCount + conceptCount <= maxQuestions;
  const isSubQuestionModalOpen = isCreateSubQuestionModalOpen || Boolean(selectedSubQuestion);
  const maxSubQuestionLibraryQuestions = Math.max(
    0,
    maxQuestions - currentQuestionCount + questionCount
  );

  const handleAddConceptModalClose = () => {
    setIsAddConceptModalOpen(false);
  };

  const handleAddConceptClick = () => {
    setIsAddConceptModalOpen(true);
  };

  const handleAddSubQuestionClick = () => {
    if (!hasQuestionLibrary) {
      setIsCreateSubQuestionModalOpen(true);
      return;
    }

    setIsAddSubQuestionPopoverOpen(true);
  };

  const handleCreateSubQuestionClick = () => {
    setIsAddSubQuestionPopoverOpen(false);
    setIsCreateSubQuestionModalOpen(true);
  };

  const handleQuestionLibraryClick = () => {
    setIsAddSubQuestionPopoverOpen(false);
    setIsQuestionLibraryModalOpen(true);
  };

  const handleQuestionLibraryModalClose = () => {
    setIsQuestionLibraryModalOpen(false);
  };

  const handleSubQuestionModalClose = () => {
    setIsCreateSubQuestionModalOpen(false);
    setSelectedSubQuestion(null);
    setSelectedSubQuestionIndex(null);
  };

  const handleSectionRemove = () => {
    onRemoveQuestion(question);
    setIsDropdownActive(false);
  };

  const getSubQuestionCostForValidation = ({ type }) =>
    type === QUESTION_TYPES.INSTRUCTIONS.value ? 0 : conceptCount;

  const getActiveAndDeletedSubQuestions = (subQuestions = []) => ({
    activeSubQuestions: subQuestions.filter(({ _delete }) => !_delete),
    deletedSubQuestions: subQuestions.filter(({ _delete }) => _delete),
  });

  const getMatchingActiveSubQuestionIndex = (activeSubQuestions, subQuestion, subQuestionIndex) =>
    subQuestion?.id !== null && subQuestion?.id !== undefined
      ? activeSubQuestions.findIndex(({ id }) => id === subQuestion.id)
      : subQuestionIndex;

  const getUpdatedQuestionsWithSubQuestions = (getSubQuestions) =>
    survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      return {
        ...surveyQuestion,
        subQuestions: getSubQuestions(surveyQuestion.subQuestions || []),
      };
    });

  const handleConceptCreate = ({ conceptImage, conceptName }) => {
    if (!conceptName.trim()) return;

    const newConcept = {
      _delete: false,
      image: conceptImage || null,
      name: conceptName.trim(),
    };
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      return {
        ...surveyQuestion,
        concepts: [...(surveyQuestion.concepts || []), newConcept],
      };
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleAddConceptModalClose();
  };

  const handleConceptRemove = (conceptIndex) => {
    const updatedQuestions = survey.questions.map((surveyQuestion) => {
      const isMatchingQuestion = surveyQuestion.id === question.id;

      if (!isMatchingQuestion) return surveyQuestion;

      const updatedConcepts = (surveyQuestion.concepts || []).map(
        (surveyConcept, surveyConceptIndex) => {
          if (surveyConceptIndex !== conceptIndex) return surveyConcept;
          return { ...surveyConcept, _delete: true };
        }
      );

      return {
        ...surveyQuestion,
        concepts: updatedConcepts,
      };
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    setActiveConceptActionsIndex(null);
  };

  const handleSubQuestionCreate = (subQuestion) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);

      return [...activeSubQuestions, subQuestion, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleSubQuestionModalClose();
  };

  const handleLibrarySubQuestionsAdd = (subQuestionsToAdd) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const librarySubQuestions = subQuestionsToAdd.map(duplicateSubQuestion);

      return [...activeSubQuestions, ...librarySubQuestions, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
    handleQuestionLibraryModalClose();
  };

  const handleSubQuestionEdit = (subQuestion, subQuestionIndex) => {
    setSelectedSubQuestion(subQuestion);
    setSelectedSubQuestionIndex(subQuestionIndex);
  };

  const handleSubQuestionDuplicate = (subQuestion, subQuestionIndex) => {
    const subQuestionCost = getSubQuestionCostForValidation(subQuestion);
    if (currentQuestionCount + subQuestionCost > maxQuestions) return;

    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const duplicateIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        subQuestion,
        subQuestionIndex
      );

      if (duplicateIndex < 0) return subQuestions;

      const updatedSubQuestions = [...activeSubQuestions];
      updatedSubQuestions.splice(duplicateIndex + 1, 0, duplicateSubQuestion(subQuestion));

      return [...updatedSubQuestions, ...deletedSubQuestions];
    });

    updateSurvey({ questions: updatedQuestions }, { markDirty: true });
  };

  const handleSubQuestionRemove = (subQuestion, subQuestionIndex) => {
    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const removeIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        subQuestion,
        subQuestionIndex
      );

      if (removeIndex < 0) return subQuestions;

      const removedSubQuestion = activeSubQuestions[removeIndex];
      const updatedActiveSubQuestions = activeSubQuestions.filter(
        (_, index) => index !== removeIndex
      );
      const updatedDeletedSubQuestions =
        typeof removedSubQuestion.id === 'number'
          ? [...deletedSubQuestions, { ...removedSubQuestion, _delete: true }]
          : deletedSubQuestions;

      return [...updatedActiveSubQuestions, ...updatedDeletedSubQuestions];
    });

    updateSurvey(
      {
        questionLogic: cleanupLogicAfterRemoval({
          question: subQuestion,
          questionId: subQuestion.id,
          questionLogic: survey.questionLogic || {},
          questions: updatedQuestions,
        }),
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    handleSubQuestionModalClose();
  };

  const handleSubQuestionUpdate = (subQuestion) => {
    if (!selectedSubQuestion) return;

    const updatedQuestions = getUpdatedQuestionsWithSubQuestions((subQuestions) => {
      const { activeSubQuestions, deletedSubQuestions } =
        getActiveAndDeletedSubQuestions(subQuestions);
      const updateIndex = getMatchingActiveSubQuestionIndex(
        activeSubQuestions,
        selectedSubQuestion,
        selectedSubQuestionIndex
      );

      if (updateIndex < 0) return subQuestions;

      const updatedSubQuestions = activeSubQuestions.map((surveySubQuestion, index) =>
        index === updateIndex ? subQuestion : surveySubQuestion
      );

      return [...updatedSubQuestions, ...deletedSubQuestions];
    });

    updateSurvey(
      {
        questionLogic: cleanupLogicAfterEdit({
          originalQuestion: selectedSubQuestion,
          question: subQuestion,
          questionLogic: survey.questionLogic || {},
          questions: updatedQuestions,
        }),
        questions: updatedQuestions,
      },
      { markDirty: true }
    );
    handleSubQuestionModalClose();
  };

  return (
    <div
      className={styles['sequential-monadic-draggable-container']}
      ref={provided.innerRef}
      {...provided.draggableProps}
    >
      {isAddConceptModalOpen && (
        <ModalAddConcept onClose={handleAddConceptModalClose} onCreate={handleConceptCreate} />
      )}
      {isSubQuestionModalOpen && (
        <ModalInstantSurveyQuestion
          allowedQuestionTypes={allowedQuestionTypes}
          getQuestionCostForValidation={getSubQuestionCostForValidation}
          onClose={handleSubQuestionModalClose}
          onCreate={handleSubQuestionCreate}
          onUpdate={handleSubQuestionUpdate}
          originalQuestion={selectedSubQuestion}
          pipingQuestionsMap={pipingQuestionsMap}
          shouldPersistOnCreate={false}
          shouldPersistOnUpdate={false}
        />
      )}
      {isQuestionLibraryModalOpen && (
        <ModalQuestionLibrary
          allowedQuestionTypes={allowedQuestionTypes}
          getQuestionCostForValidation={getSubQuestionCostForValidation}
          maxQuestions={maxSubQuestionLibraryQuestions}
          onClose={handleQuestionLibraryModalClose}
          onSelectQuestions={handleLibrarySubQuestionsAdd}
          selectedQuestions={activeSubQuestions}
          surveyType={instantSurveyType}
        />
      )}

      <Card
        as={Layout.Flex.Column}
        className={styles['sequential-monadic-card']}
        elevation={2}
        gap="small"
      >
        <div className={styles['sequential-monadic-header']}>
          <div className={styles['sequential-monadic-title-wrapper']}>
            <div className={styles['sequential-monadic-drag-handle']} {...provided.dragHandleProps}>
              <DragDropV2Icon />
            </div>
            <h3>Monadic Section</h3>
            <ShuffleIcon />
            <Pill isCompleted value={questionCountLabel} />
          </div>
          {showActions && (
            <div className={styles['sequential-monadic-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
                target={optionsDropdown.current}
              >
                <ul>
                  <li>
                    <button
                      aria-label="Remove monadic section"
                      data-testid="remove-sequential-monadic-section-button"
                      onClick={handleSectionRemove}
                    >
                      <TrashIcon />
                      <span>Remove Section</span>
                    </button>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </div>

        <p className={styles['sequential-monadic-copy']}>
          This section of questions will show the following concepts.
        </p>
        <p className={styles['sequential-monadic-copy']}>
          {randomizationLabel}Panelists will see all of these questions {conceptCount} time
          {conceptCount === 1 ? '' : 's'} based on the following concepts:
        </p>

        {showActions && (
          <Button
            className={styles['sequential-monadic-add-concept']}
            icon={<PlusIcon />}
            onClick={handleAddConceptClick}
            text="Add Concept"
            variant="secondary"
          />
        )}

        {activeConcepts.length > 0 && (
          <ol className={styles['sequential-monadic-concepts']}>
            {activeConcepts.map(({ concept, conceptIndex }, index) => {
              const imageUrl = getImageUrl(concept.image);

              const conceptName = concept.name || `Concept ${index + 1}`;

              return (
                <li
                  className={styles['sequential-monadic-concept']}
                  key={concept.id ?? conceptIndex}
                >
                  {showActions && (
                    <div className={styles['sequential-monadic-concept-actions']}>
                      <ActionOverlayPopover
                        hasArrow={false}
                        isActive={activeConceptActionsIndex === conceptIndex}
                        onClick={(event) => {
                          event.stopPropagation();
                          setActiveConceptActionsIndex(
                            activeConceptActionsIndex === conceptIndex ? null : conceptIndex
                          );
                        }}
                        onHide={() => setActiveConceptActionsIndex(null)}
                      >
                        <ul>
                          <li>
                            <button
                              aria-label={`Remove ${conceptName}`}
                              onClick={() => handleConceptRemove(conceptIndex)}
                            >
                              <TrashIcon />
                              <span>Remove Concept</span>
                            </button>
                          </li>
                        </ul>
                      </ActionOverlayPopover>
                    </div>
                  )}
                  <div className={styles['sequential-monadic-concept-image-wrapper']}>
                    {imageUrl && (
                      <img
                        alt={conceptName}
                        className={styles['sequential-monadic-concept-image']}
                        src={imageUrl}
                      />
                    )}
                  </div>
                  <span className={styles['sequential-monadic-concept-name']}>{conceptName}</span>
                </li>
              );
            })}
          </ol>
        )}

        <div className={styles['sequential-monadic-questions']}>
          {showActions && activeSubQuestions.length === 0 && (
            <button
              className={styles['sequential-monadic-subquestion-placeholder']}
              onClick={handleCreateSubQuestionClick}
              type="button"
            >
              <span
                aria-hidden
                className={styles['sequential-monadic-subquestion-placeholder-drag-handle']}
              >
                <DragDropV2Icon />
              </span>
              <span className={styles['sequential-monadic-subquestion-placeholder-number']}>1</span>
              <span className={styles['sequential-monadic-subquestion-placeholder-text']}>
                Click to create a question
              </span>
            </button>
          )}

          {activeSubQuestions.length > 0 && (
            <Droppable
              droppableId={subQuestionDroppableId}
              isDropDisabled={shouldDisableSubQuestionDrag}
              type={SEQUENTIAL_MONADIC_SUBQUESTION_DROPPABLE_TYPE}
            >
              {(droppableProvided) => (
                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                  {activeSubQuestions.map((subQuestion, index) => {
                    const subQuestionId = subQuestion.id ?? index;

                    return (
                      <Draggable
                        draggableId={`sequential-monadic-subquestion-${question.id}-${subQuestionId}`}
                        index={index}
                        isDragDisabled={shouldDisableSubQuestionDrag}
                        key={subQuestionId}
                      >
                        {(draggableProvided) => (
                          <Question
                            canPreviewSurvey={canPreviewSurvey}
                            firstErrorRef={firstErrorRef}
                            index={index}
                            isDetailedQuestionView={isDetailedQuestionView}
                            isSequentialMonadicSubQuestion
                            number={index + 1}
                            onDuplicateQuestion={() =>
                              handleSubQuestionDuplicate(subQuestion, index)
                            }
                            onEditQuestion={() => handleSubQuestionEdit(subQuestion, index)}
                            onPreviewSurvey={onPreviewSurvey}
                            onRemoveQuestion={() => handleSubQuestionRemove(subQuestion, index)}
                            provided={draggableProvided}
                            question={subQuestion}
                            questionLogic={questionLogic || {}}
                            selectedQuestions={selectedQuestions}
                            setQuestionLogic={setQuestionLogic}
                            showActions={showActions}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          )}

          {showActions && (
            <div className={styles['sequential-monadic-add-subquestion-wrapper']}>
              <IconButton
                aria-label="Add monadic subquestion"
                className={styles['sequential-monadic-add-subquestion']}
                isDisabled={!canAddSubQuestion}
                onClick={handleAddSubQuestionClick}
                ref={addSubQuestionButtonRef}
                tooltip={canAddSubQuestion ? 'Add Question' : maxQuestionError}
                type="button"
              >
                <PlusIcon />
              </IconButton>
              {hasQuestionLibrary && (
                <OverlayPopover
                  className={styles['sequential-monadic-add-subquestion-dropdown']}
                  isActive={isAddSubQuestionPopoverOpen}
                  onHide={() => setIsAddSubQuestionPopoverOpen(false)}
                  target={addSubQuestionButtonRef.current}
                >
                  <ul>
                    <li>
                      <button onClick={handleCreateSubQuestionClick}>
                        <span>Create Question</span>
                        <PlusIcon />
                      </button>
                    </li>
                    <li>
                      <button onClick={handleQuestionLibraryClick}>
                        <span>Add From Library</span>
                        <PlusIcon />
                      </button>
                    </li>
                  </ul>
                </OverlayPopover>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

SequentialMonadicBlock.propTypes = {
  canPreviewSurvey: PropTypes.bool.isRequired,
  firstErrorRef: PropTypes.object.isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  onPreviewSurvey: PropTypes.func.isRequired,
  onRemoveQuestion: PropTypes.func.isRequired,
  pipingQuestionsMap: PropTypes.object.isRequired,
  provided: PropTypes.object,
  question: PropTypes.object.isRequired,
  questionLogic: PropTypes.object,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
};

export default SequentialMonadicBlock;
